import { Provider } from '@/components/ui/provider';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';

import { chains } from '@/data/chains';
import { projectId } from '@/lib/wagmi';
import { makeRequest } from '@/network/api';
import { parseAddressFromDID, trackUserLogin } from '@/utils';
import { WagmiAdapter } from '@reown/appkit-adapter-wagmi';
import { createSIWEConfig, type SIWECreateMessageArgs, type SIWEVerifyMessageArgs } from '@reown/appkit-siwe';
import { defineChain, type AppKitNetwork } from '@reown/appkit/networks';
import { createAppKit } from '@reown/appkit/react';
import { PropsWithChildren } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { BrowserRouter } from 'react-router';
import { Address } from 'viem';
import { mainnet, plumeMainnet } from 'viem/chains';
import { createSiweMessage, generateSiweNonce } from 'viem/siwe';
import { WagmiProvider } from 'wagmi';
import { AnalyticsProvider } from './analytics-provider';
import { AuthProvider } from './auth-provider';
import { HomeBannerProvider } from './home-banner-provider';

// If no trackingId is provided, analytics will not be initialized
const GA_TRACKING_ID = import.meta.env.VITE_GA_TRACKING_ID || '';

const queryClient = new QueryClient();

const metadata = {
  name: 'Portal Frontend',
  description: 'Frontend for the Plume Portal',
  url: 'https://portal.plume.org',
  icons: ['/assets/plume-logo-icon.svg'],
};

// Convert chains from chains.ts to viem chain format
const viemChains = chains.map((chain) =>
  defineChain({
    id: chain.id,
    name: chain.name,
    chainNamespace: 'eip155',
    caipNetworkId: `eip155:${chain.id}`,
    nativeCurrency: {
      name: chain.metamask.nativeCurrency.name,
      symbol: chain.metamask.nativeCurrency.symbol,
      decimals: chain.metamask.nativeCurrency.decimals,
    },
    rpcUrls: {
      default: {
        http: chain.metamask.rpcUrls,
      },
    },
    blockExplorers:
      chain.metamask.blockExplorerUrls.length > 0
        ? {
            default: {
              name: `${chain.name} Explorer`,
              url: chain.metamask.blockExplorerUrls[0],
              apiUrl: chain.metamask.blockExplorerUrls[0],
            },
          }
        : undefined,
    testnet: !chain.mainnet,
  }),
);

// Get IDs of predefined chains to avoid duplicates
const predefinedChainIds = new Set<number>([mainnet.id, plumeMainnet.id]);

// Filter out chains that are already defined in viem/chains
const uniqueViemChains = viemChains.filter((chain) => {
  const chainId: number = Number(chain.id);
  return !predefinedChainIds.has(chainId);
});

// Combine predefined chains with converted chains, avoiding duplicates
const allNetworks: [AppKitNetwork, ...AppKitNetwork[]] = [mainnet, plumeMainnet, ...uniqueViemChains] as [
  AppKitNetwork,
  ...AppKitNetwork[],
];

const adapter = new WagmiAdapter({
  networks: allNetworks,
  projectId,
  ssr: false,
});

const siweConfig = createSIWEConfig({
  signOutOnNetworkChange: false,
  getMessageParams: async () => ({
    domain: window.location.host,
    uri: window.location.origin,
    chains: [plumeMainnet.id],
    chainId: plumeMainnet.id,
    statement: 'Sign this message for auth.',
    expirationTime: new Date(Date.now() + 1000 * 60 * 60 * 24),
  }),
  createMessage: ({ address, ...args }: SIWECreateMessageArgs) =>
    createSiweMessage({
      ...args,
      address: parseAddressFromDID(address) as Address,
      chainId: plumeMainnet.id,
    }),
  getNonce: async () => generateSiweNonce(),
  getSession: async () => makeRequest('/auth/checkSession').then((res) => res.json()),
  verifyMessage: async ({ message, signature }: SIWEVerifyMessageArgs) => {
    try {
      const res = await makeRequest('/auth/login', {
        method: 'POST',
        body: JSON.stringify({ message, signature }),
      });
      if (res.ok) {
        return true;
      }
      console.error('Error verifying message', res.statusText);
      return false;
    } catch (error) {
      console.error('Error verifying message', error);
      return false;
    }
  },
  onSignIn: (session) => {
    queryClient.invalidateQueries();
    if (session?.address) {
      trackUserLogin(session.address);
    }
  },
  onSignOut: () => {
    queryClient.invalidateQueries();
  },
  signOut: async () => {
    try {
      const res = await makeRequest('/auth/logout', { method: 'POST' });
      if (res.ok) {
        return true;
      }
      console.error('Error signing out', res.statusText);
      return false;
    } catch (error) {
      console.error('Error signing out', error);
      return false;
    }
  },
});

createAppKit({
  adapters: [adapter],
  networks: allNetworks,
  siweConfig,
  projectId,
  metadata,
  features: {
    analytics: true, // Optional - defaults to your Cloud configuration
    socials: false,
    email: false,
  },
  themeVariables: {
    '--w3m-z-index': 100000,
  },
});

export const Providers = ({ children }: PropsWithChildren) => {
  return (
    <ErrorBoundary
      fallback={<p>⚠️ Something went wrong, please try again later</p>}
      onError={(error, errorInfo) => {
        console.error('Error:', error, errorInfo);
      }}
    >
      <WagmiProvider config={adapter.wagmiConfig}>
        <QueryClientProvider client={queryClient}>
          <BrowserRouter>
            <AuthProvider>
              <Provider>
                <HomeBannerProvider>
                  <AnalyticsProvider trackingId={GA_TRACKING_ID}>{children}</AnalyticsProvider>
                </HomeBannerProvider>
              </Provider>
              {/* ReactQueryDevtools is automatically excluded from production builds so does not need to be conditionally rendered */}
              <ReactQueryDevtools initialIsOpen={false} />
            </AuthProvider>
          </BrowserRouter>
        </QueryClientProvider>
      </WagmiProvider>
    </ErrorBoundary>
  );
};
